"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage1 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig1.avif";
import BannerImage2 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig2.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenPrivaterAutoverkaufKaufvertrag } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function PrivaterAutoverkaufKaufvertrag() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Privater Autoverkauf Kaufvertrag – rechtssicher & einfach erklärt"
        link="/guides/privater-autoverkauf-kaufvertrag"
        linkname="Privater Autoverkauf Kaufvertrag"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Das sollten Käufer und Verkäufer wissen"
              leftText={
                <>
                  <p>
                    Beim privaten Autoverkauf gilt: Vertrauen ist gut, ein
                    schriftlicher Kaufvertrag ist besser.
                  </p>
                  <p>
                    Nur ein klar formulierter Vertrag schützt dich vor
                    Missverständnissen, Nachforderungen oder Haftungsrisiken.
                  </p>
                  <p>
                    Er sorgt dafür, dass Käufer und Verkäufer rechtlich
                    abgesichert sind – und der Fahrzeugverkauf korrekt abgewickelt
                    wird.
                  </p>
                  <p>
                    Wenn du dein Auto nicht selbst verkaufen möchtest oder
                    rechtliche Unsicherheiten vermeiden willst, bietet dir
                    Autoeinfachlos eine sichere und stressfreie Alternative:
                    Verkauf über geprüfte Händler – mit fairen Preisen und
                    garantierter Auszahlung.
                  </p>
                </>
              }
              rightFirstTitle="Warum ein Kaufvertrag beim privaten Autoverkauf so wichtig ist"
              rightFirstText={
                <>
                  <p>
                    Ohne schriftlichen Kaufvertrag fehlt die rechtliche Grundlage
                    für den Verkauf.
                  </p>
                  <p>
                    Ein formloser oder mündlicher Deal kann schnell zu Streit
                    führen, etwa wenn:
                  </p>
                  <ul className="custom-unordered-list">
                    <li>der Käufer nachträglich Mängel reklamiert,</li>
                    <li>der Kaufpreis nicht vollständig bezahlt wird oder</li>
                    <li>
                      unklar bleibt, wann das Fahrzeug tatsächlich übergeben
                      wurde.
                    </li>
                  </ul>
                  <p>
                    Ein schriftlicher Kaufvertrag schafft hier Sicherheit für
                    beide Seiten.
                  </p>
                  <p>
                    Er dokumentiert den Zustand des Autos, den vereinbarten Preis
                    und schützt dich vor späteren Ansprüchen.
                  </p>
                  <p>
                    Zudem ist er die Voraussetzung, um die Gewährleistung wirksam
                    auszuschließen.
                  </p>

                  <h3>Was muss im Kaufvertrag für den privaten Autoverkauf stehen?</h3>
                  <p>
                    Damit der Vertrag rechtlich gültig ist, müssen folgende
                    Angaben enthalten sein:
                  </p>
                  <ul className="custom-unordered-list">
                    <li>Name und Anschrift von Käufer und Verkäufer</li>
                    <li>
                      Fahrzeugdaten: Marke, Modell, Baujahr, Kennzeichen,
                      Fahrgestellnummer (FIN)
                    </li>
                    <li>Kaufpreis und vereinbarte Zahlungsart</li>
                    <li>Datum und Ort der Übergabe</li>
                    <li>Bekannte Mängel oder Schäden am Fahrzeug</li>
                    <li>Unterschriften beider Parteien</li>
                  </ul>
                  <p>
                    Ergänze bei Bedarf Hinweise zur Probefahrt, zur
                    Fahrzeugpapiere-Übergabe oder zur Abmeldung.
                  </p>
                  <p>
                    Nur mit vollständigen Angaben ist der Vertrag rechtssicher und
                    nachvollziehbar.
                  </p>
                </>
              }
              rightSecondTitle="Kaufvertrag Auto privat – Muster & Vorlage"
              rightSecondText={
                <>
                  <p>
                    Wenn du dein Auto privat verkaufst, lohnt sich eine
                    standardisierte Vorlage – etwa vom ÖAMTC, ADAC oder direkt von
                    Autoeinfachlos.
                  </p>
                  <p>
                    Ein besonders wichtiger Punkt ist der Haftungsausschluss. Eine
                    rechtssichere Formulierung lautet:
                  </p>
                  <p>
                    „Das Fahrzeug wird unter Ausschluss jeglicher Gewährleistung
                    verkauft. Der Verkäufer haftet nicht für Sachmängel.“
                  </p>
                  <p>
                    Achtung: Dieser Ausschluss gilt nur, wenn du privat und nicht
                    gewerblich verkaufst. Bist du Unternehmer oder handelst
                    regelmäßig mit Fahrzeugen, kannst du die Gewährleistung nicht
                    ausschließen.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="Haftung & Gewährleistung beim privaten Autoverkauf"
                alt="Haftung und Gewährleistung beim privaten Autoverkauf"
                text={
                  <>
                    <p>
                      Beim privaten Autoverkauf gilt grundsätzlich: Du darfst die
                      Gewährleistung ausschließen, bist aber trotzdem verpflichtet,
                      bekannte Mängel ehrlich anzugeben.
                    </p>
                    <p>
                      Ausnahme: Wenn du Mängel arglistig verschweigst, bleibt die
                      Haftung bestehen, selbst bei Ausschlussklausel.
                    </p>
                    <p>
                      Deshalb gilt:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>
                        Alle sichtbaren oder bekannten Schäden im Vertrag
                        vermerken
                      </li>
                      <li>
                        Keine falschen Zusicherungen machen („unfallfrei“,
                        „Service neu“ usw.)
                      </li>
                      <li>Bei Unsicherheiten lieber transparent bleiben</li>
                    </ul>
                    <p>
                      Weitere Details findest du im Ratgeber:{" "}
                      <Link
                        className="link"
                        href="/guides/gewaehrleistung-privatverkauf-auto"
                      >
                        Gewährleistung Privatverkauf Auto
                      </Link>
                    </p>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage2}
                h2
                isBig
                fontSize="36px"
                title="Auto privat verkaufen - Risiken und Alternativen"
                alt="Risiken und Alternativen beim privaten Autoverkauf"
                text={
                  <>
                    <p>
                      Ein Privatverkauf klingt unkompliziert, bringt aber häufig
                      Risiken mit sich:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>Betrugsversuche bei Online-Plattformen</li>
                      <li>Zahlungsverzug oder ungedeckte Überweisungen</li>
                      <li>Probleme bei der Übergabe oder Abmeldung</li>
                      <li>
                        Rechtliche Streitigkeiten wegen angeblicher Mängel
                      </li>
                    </ul>
                    <p>
                      Wenn du auf Nummer sicher gehen willst, verkaufe dein Auto
                      über Autoeinfachlos:
                    </p>
                    <p>
                      Wir vermitteln dein Fahrzeug an über 5.000 geprüfte Händler
                      in Österreich – mit fairen Angeboten, sicherer Zahlung und
                      ohne Papierkram.
                    </p>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="Ablauf beim sicheren Autoverkauf mit Autoeinfachlos"
                alt="Ablauf beim sicheren Autoverkauf mit Autoeinfachlos"
                text={
                  <>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Auto kostenlos online bewerten</strong> – In nur
                        einer Minute erfährst du den aktuellen Marktwert.
                      </li>
                      <li>
                        <strong>24-Stunden-Auktion</strong> – Über 5.000 Händler
                        geben verbindliche Gebote ab.
                      </li>
                      <li>
                        <strong>Bestes Angebot wählen</strong> – Du entscheidest,
                        an wen verkauft wird.
                      </li>
                      <li>
                        <strong>Abholung & Auszahlung</strong> – Fahrzeug wird
                        direkt bei dir abgeholt, das Geld sicher überwiesen.
                      </li>
                    </ul>
                    <p>
                      👉{" "}
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten
                      </Link>
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Kaufvertrag beim privaten Autoverkauf"
              fragen={fragenPrivaterAutoverkaufKaufvertrag}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Privater Autoverkauf Kaufvertrag"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Kaufvertrag richtig machen – sicher verkaufen"
                alt="Kaufvertrag richtig machen und sicher verkaufen"
                text={
                  <>
                    <p>
                      Ein klarer, vollständiger Kaufvertrag schützt dich beim
                      privaten Autoverkauf vor Streit, Nachforderungen und
                      rechtlichen Risiken.
                    </p>
                    <p>
                      Wer auf Nummer sicher gehen möchte, nutzt den
                      professionellen Weg über Autoeinfachlos: Hier übernehmen
                      erfahrene Profis den kompletten Verkaufsprozess -
                      rechtssicher, schnell und ohne Risiko.
                    </p>
                    <p>
                      👉{" "}
                      <Link className="link" href="/auto-verkaufen">
                        Jetzt Auto kostenlos bewerten & sicher verkaufen
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default PrivaterAutoverkaufKaufvertrag;
